import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useRouter } from '@tanstack/react-router';

import { useLogin } from '@/api/generated';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import { type LoginSchema, loginSchema } from '@/routes/(auth)/lib/schema';

export const LoginForm = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
  });
  const { mutate } = useLogin();
  const router = useRouter();

  const onSubmit = useCallback(
    async (data: LoginSchema) => {
      mutate(
        { data },
        {
          onSuccess: async (response) => {
            login(response);
            await router.invalidate();
          },
        }
      );
    },
    [login, mutate, router]
  );

  return (
    <div
      className={cn('flex flex-col gap-6', className)}
      {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid !p-0 md:grid-cols-2'>
          <Form {...form}>
            <form
              className='p-6 md:p-8'
              onSubmit={form.handleSubmit(onSubmit)}>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col items-center text-center'>
                  <h1 className='text-2xl font-bold'>Welcome back</h1>
                  <p className='text-muted-foreground text-balance'>
                    Login to your Acme Inc account
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name='username'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.login.username')}</FormLabel>
                      <FormControl>
                        <Input
                          id='username'
                          placeholder='m@example.com'
                          required
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.login.password')}</FormLabel>
                      <FormControl>
                        <Input
                          id='password'
                          type='password'
                          placeholder='********'
                          required
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type='submit'
                  className='w-full'>
                  {t('auth.login.submit')}
                </Button>
                <div className='after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t'>
                  <span className='bg-background text-muted-foreground relative z-10 px-2'>
                    {t('auth.login.orContinueWith')}
                  </span>
                </div>
                <div className='flex items-center justify-center gap-2'>
                  <Button variant='outline'>
                    <Button.Icon icon>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 24 24'>
                        <path
                          d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701'
                          fill='currentColor'
                        />
                      </svg>
                    </Button.Icon>

                    <span className='sr-only'>{t('auth.login.apple')}</span>
                  </Button>
                  <Button variant='outline'>
                    <Button.Icon icon>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 24 24'>
                        <path
                          d='M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z'
                          fill='currentColor'
                        />
                      </svg>
                    </Button.Icon>
                    <span className='sr-only'>{t('auth.login.google')}</span>
                  </Button>
                </div>
                <div className='text-center text-sm'>
                  <Link
                    to='/auth/login'
                    className='underline underline-offset-4'>
                    {t('auth.login.register')}
                  </Link>
                </div>
              </div>
            </form>
            <div className='bg-muted relative hidden md:block'>
              <img
                src='/placeholder.svg'
                alt='brand'
                className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
              />
            </div>
          </Form>
        </CardContent>
      </Card>
      <div className='text-muted-foreground hover:[&_a]:text-primary text-balance text-center text-xs [&_a]:underline [&_a]:underline-offset-4'>
        <Trans
          i18nKey={'toc'}
          components={{ a: <Link to='/' /> }}
        />
      </div>
    </div>
  );
};
